import { get } from 'svelte/store';

//Helpers
import { SLEEPING_TYPES } from '../constants';
import { get_local_ip, real_id } from '../helpers/interface_helpers';
import {
	get_fresh_verified_local_ip,
	refresh_verified_local_ip,
} from '../helpers/local_ip_probe_helpers';
import { socket_send } from './socket_commands';
import { localReq } from './http';
import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';

//Stores
import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';

//Types
import type { Request } from '../types/requests/request';
import type { RequestDataGen2 } from '../types/requests/request_data_g2';
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';

export const gen2CloudReq = async (
	id: string,
	type: string,
	method: string,
	params: {},
	viaq?: boolean,
	timeout?: number,
) => {
	let res;
	let data: RequestDataGen2 = {
		id,
		method,
		params: JSON.stringify(params),
	};

	if (typeof viaq === 'undefined' && SLEEPING_TYPES.includes(type)) {
		data = { ...data, viaq: true };
		viaq = true;
	} else if (viaq) {
		data = { ...data, viaq };
	}

	try {
		res = await socket_send(id, params, 'Shelly:JrpcRequest', method, viaq, timeout);
	} catch (error) {
		res = { error: error.message };
	} finally {
		if (!res) {
			return { isok: false };
		}
		// change result key with data for unification
		if (res?.hasOwnProperty('result')) {
			res = { isok: true, data: res.result };
		}
		// include isok property
		if (!res?.hasOwnProperty('isok')) {
			res = {
				isok: !res?.hasOwnProperty('error'),
				...res,
			};
		}
	}

	return res;
};

export const gen2Request = async (
	device: Device,
	method: string,
	params: {},
	viaq?: boolean,
	skip_local: boolean = false,
	timeout?: number,
) => {
	const local_ip = get_local_ip(real_id(device.id));

	let res: Request;
	if (local_ip && !skip_local) {
		res = await localReq(device, `${local_ip}/rpc/`, 'post', {
			id: Date.now(),
			method,
			params,
			src: 'ShellyApp',
		});

		if (!res?.isok) {
			const cachedIp = get_fresh_verified_local_ip(real_id(device.id));
			if (cachedIp && cachedIp !== local_ip) {
				res = await localReq(device, `${cachedIp}/rpc/`, 'post', {
					id: Date.now(),
					method,
					params,
					src: 'ShellyApp',
				});
			}
			if (!res?.isok) {
				void refresh_verified_local_ip(device.id, { debounced: true });
			}
		}
	} else {
		let deviceID = real_id(device.id);
		if (device.shared && (isVirtualDevice(device.type) || isPeripheralDevice(device.type))) {
			deviceID = device.id;
		}
		res = await gen2CloudReq(deviceID, device.type, method, params, viaq, timeout);
	}

	if (!res?.isok && local_ip && typeof get(cloud_disabled_devices)[device.id] === 'undefined') {
		res = await gen2CloudReq(device.id, device.type, method, params, viaq, timeout);
	}
	return res;
};

export class ChunkRequest {
	protected _device: Device;
	protected _method: string;
	protected _params: any;
	protected _viaq: boolean;
	protected _abort = false;
	protected _offset = 0;
	protected _total = 0;
	protected _res: SettingRequest;

	/**
	 * @param device The device to which requests will be sent
	 * @param method The method to be invoked on the device
	 * @param params Parameters to be passed with the request
	 * @param viaq Optional flag to indicate a specific cloud request for sleeping devices
	 */
	constructor(device: Device, method: string, params = {}, viaq?: boolean) {
		this._device = device;
		this._method = method;
		this._params = params;
		this._viaq = viaq;
		this.reset();
	}
	/**
	 * Public method to fetch all chunks
	 * @returns A promise that resolves to the accumulated result
	 */
	public async get(): Promise<SettingRequest> {
		this.reset();
		while (this.hasNext()) {
			await this.request();
		}
		return this._res;
	}
	/**
	 * Public method to fetch the next chunk
	 * @returns A promise that resolves to the result after fetching the chunk
	 */
	public async getNext(): Promise<SettingRequest> {
		await this.request();
		return this._res;
	}
	/**
	 * Public method to check if there are more chunks to fetch
	 * @returns A boolean indicating if more data is available
	 */
	public hasNext(): boolean {
		return !this._abort && this._offset < this._total;
	}
	/**
	 * Public method to abort the request process
	 */
	public abort() {
		this._abort = true;
	}
	/**
	 * Public method to check if the request process is aborted
	 * @returns A boolean indicating if the request process is aborted
	 */
	public isAborted(): boolean {
		return this._abort;
	}
	/**
	 * Public method to reset the internal state
	 */
	public reset() {
		this._abort = false;
		this._offset = 0;
		this._total = 1;
		this._res = {
			isok: false,
			data: {},
		};
	}
	/**
	 * Protected method to perform a single request
	 */
	protected async request() {
		let res: SettingRequest = await gen2Request(
			this._device,
			this._method,
			{ ...this._params, offset: this._offset },
			this._viaq,
		);
		if (res?.isok) {
			this._res.isok = true;
			const data = res?.data || res;
			if (data?.offset != null && data?.total != null) {
				const reqParams = Object.keys(data).filter((i) => !/^(offset|total)$/.test(i));
				const paramKey = reqParams?.[0];
				const param = data?.[paramKey];
				if (Array.isArray(param) && param?.length) {
					this._res.data[paramKey] = [...(this._res.data?.[paramKey] || []), ...param];
					this._offset += param?.length;
					this._total = data?.total || 0;
					return;
				}
			}
			this._res.data = data;
		} else {
			this._res = res;
		}
		this._total = 0;
	}
}

export class BytesChunkRequest extends ChunkRequest {
	private _chunkLen = 0;
	private _chunkLeft = 0;
	/**
	 * @param device The device to which requests will be sent.
	 * @param method The method to be invoked on the device.
	 * @param params Parameters to be passed with the request.
	 * @param chunkLen The length of each chunk in bytes.
	 */
	constructor(device: Device, method: string, params = {}, chunkLen = 1024) {
		super(device, method, params);
		this._chunkLen = chunkLen;
	}
	override hasNext(): boolean {
		return !this._abort && this._chunkLeft > 0;
	}
	override reset() {
		this._abort = false;
		this._offset = 0;
		this._chunkLeft = 1;
		this._res = {
			isok: false,
			data: {
				data: '',
			},
		};
	}
	protected override async request() {
		const len = this._chunkLen;
		const maxRetries = 3;
		for (let attempt = 0; attempt < maxRetries; attempt++) {
			const res: SettingRequest = await gen2Request(
				this._device,
				this._method,
				{ ...this._params, len, offset: this._offset },
				false,
			);
			if (res?.isok) {
				this._res.isok = true;
				const data = res?.data?.data != null ? res?.data : res;
				if (data?.left != null && typeof data?.data == 'string') {
					this._chunkLeft = data.left;
					this._res.data.data += data.data;
					this._offset += len;
					return;
				}
				this._res.data = data;
				this._chunkLeft = 0;
				return;
			}
			if (attempt < maxRetries - 1) {
				await new Promise((r) => setTimeout(r, 1000));
				continue;
			}
			this._res.isok = false;
			this._chunkLeft = 0;
		}
	}
}
